import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  GetSingleWorkoutAsync,
  removeParticipantAsync,
  selectEditFlag,
  selectSingleWorkout,
  setEditFlag,
  setSuccessFlag,
  updateParticipantsAsync,
} from '../features/add_workout/workoutSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { calibers, changeDateFormat, participant } from '../app/consts';
import { Button, Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import TranzilaPaymentModal from './TranzilaPaymentModal';
import './style/register.css';
import { server } from "../app/consts"

const Register = () => {
  const workoutId = useParams().workoutId;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Form states
  const [FirstName, setFirstName] = useState('');
  const [LastName, setLastName] = useState('');
  const [PhoneNum, setPhoneNum] = useState('');
  const [LicenseNumber, setLicenseNumber] = useState('');
  const [LicenseExpiryDate, setLicenseExpiryDate] = useState('');
  const [PistolType, setPistolType] = useState('');
  const [Caliber, setCaliber] = useState('');
  const [otherCaliber, setOtherCaliber] = useState('');
  const [PistolNumber, setPistolNumber] = useState('');
  const [Email, setEmail] = useState('');
  const [Notes, setNotes] = useState('');

  // Validations
  const [PhoneNumberValid, setPhoneNumberValid] = useState<boolean>(true);
  const [LicenseNumberValid, setLicenseNumberValid] = useState<boolean>(true);
  const [otherCaliberValid, setOtherCaliberValid] = useState<boolean>(true);
  const [FirstNameValid, setFirstNameValid] = useState<boolean>(true);
  const [LastNameValid, setLastNameValid] = useState<boolean>(true);
  const [isTermsChecked, setIsTermsChecked] = useState<boolean>(false);

  // Helpers
  const [PhoneNumberHelper, setPhoneNumberHelper] = useState('');
  const [LicenseNumberHelper, setLicenseNumberHelper] = useState('');
  const [FirstNameHelper, setFirstNameHelper] = useState('');
  const [LastNameHelper, setLastNameHelper] = useState('');
  const [otherCaliberHelper, setOtherCaliberHelper] = useState('');

  // Payment flow states
  const [isPaymentOpen, setIsPaymentOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [createdParticipantId, setCreatedParticipantId] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState<string>('');

  const currentWorkout = useAppSelector(selectSingleWorkout);
  const editFlag = useAppSelector(selectEditFlag);

  useEffect(() => {
    dispatch(GetSingleWorkoutAsync(workoutId as string));
    // eslint-disable-next-line
  }, [currentWorkout?.participantsList?.length, editFlag]);

  const createParticipantObject = (activeStatus: boolean = false): participant => {
    const now = new Date().toISOString()
    return {
      firstName: FirstName,
      lastName: LastName,
      phoneNum: PhoneNum,
      email: Email,
      licenseNumber: LicenseNumber,
      licenseExpiryDate: LicenseExpiryDate,
      pistolType: PistolType,
      caliber: Caliber === 'אחר' ? otherCaliber : Caliber,
      pistolNumber: PistolNumber,
      notes: Notes,
      isActive: activeStatus,
      createdAt: now,
    };
  };

  // 1. Initial Step / Pre-Registration
  const handleProceedToPayment = async () => {
    if (!isTermsChecked) {
      toast.error('יש לקרוא ולאשר את התנאים של האימון');
      return;
    }

    const form = !document.querySelector<HTMLInputElement>('form[id="register"]')?.checkValidity();
    let isErrorExist =
      form || !PhoneNumberValid || !LicenseNumberValid || !FirstNameValid || !LastNameValid;

    if (Caliber === 'אחר') {
      isErrorExist = isErrorExist || !otherCaliberValid;
    }

    if (isErrorExist) {
      toast.error('קיימת טעות/חסר מידע בטופס');
      return;
    }

    setIsSubmitting(true);

    const tempParticipant = createParticipantObject(true);

    try {
      const res: { uid?: string; isFree?: boolean; result: string; msg: string } = await dispatch(
        updateParticipantsAsync({ id: workoutId as string, participantInfo: tempParticipant })
      ).unwrap();

      const newUid = res?.uid || null;
      setCreatedParticipantId(newUid);

      // If free workout, backend completed registration & sent email directly
      if (Number(currentWorkout.price) === 0 || res?.isFree) {
        toast.success('ההרשמה הושלמה בהצלחה!');
        dispatch(setSuccessFlag());
        dispatch(setEditFlag());

        setTimeout(() => {
          navigate('/register/completed/new');
        }, 1500);
        return;
      }

      try {
        const apiBase = server.startsWith("http")
          ? server.replace(/\/+$/, "")
          : `${window.location.origin}${server}`.replace(/\/+$/, "");

        const paymentRes = await fetch(`${server}generate-payment-url/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workoutId: workoutId,
            uid: newUid,
            contact: `${FirstName} ${LastName}`.trim(),
            email: Email,
            phone: PhoneNum,
            origin: window.location.origin,
            apiBase: apiBase
          })
        });

        const paymentData = await paymentRes.json();

        if (paymentData.paymentUrl) {
          setPaymentUrl(paymentData.paymentUrl);
          setIsPaymentOpen(true);
        } else {
          throw new Error("Missing payment URL");
        }
      } catch (err) {
        toast.error('שגיאה ביצירת מסוף תשלום. מנתק הרשמה...');
        handleRevertRegistration();
      }

    } catch (err: any) {
      const errorMsg = err?.msg || 'אירעה שגיאה בשמירת פרטי הנרשם';
      toast.error(errorMsg);

      if (err?.msg === 'הנך כבר רשום לאימון זה') {
        setTimeout(() => navigate('/register/completed/already'), 1000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 2. Revert ONLY on Payment Cancel / Failure
  const handleRevertRegistration = useCallback(async () => {
    console.log("payment canceled - reverting");

    if (createdParticipantId && workoutId) {
      try {
        await dispatch(
          removeParticipantAsync({
            workoutId: Number(workoutId),
            participantUid: createdParticipantId,
          })
        ).unwrap();
        console.log("reversion completed successfully");
      } catch (err) {
        console.error("Failed to revert participant:", err);
      }
    } else {
      console.warn("Revert skipped: IDs are missing");
    }

    toast.warn("ההרשמה בוטלה עקב אי-השלמת תשלום");
    setIsPaymentOpen(false);
  }, [dispatch, workoutId, createdParticipantId]);

  // 3. Payment Success - Tranzila confirmed & backend updated
  const handlePaymentSuccess = useCallback(() => {
    setIsPaymentOpen(false);
    toast.success('התשלום וההרשמה הושלמו בהצלחה!');
    dispatch(setSuccessFlag());
    dispatch(setEditFlag());

    setTimeout(() => {
      navigate('/register/completed/new');
    }, 1500);
  }, [dispatch, navigate]);

  const handlePaymentFail = useCallback(() => {
    toast.error('התשלום נדחה או בוטל');
    setIsPaymentOpen(false);
  }, []);

  useEffect(() => {
    const handlePaymentMessage = (event: MessageEvent) => {
      // Security check: only accept messages from your own domain
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'TRANZILA_PAYMENT_SUCCESS') {
        handlePaymentSuccess();
      } else if (event.data?.type === 'TRANZILA_PAYMENT_FAIL') {
        handlePaymentFail();
      }
    };

    window.addEventListener('message', handlePaymentMessage);
    return () => window.removeEventListener('message', handlePaymentMessage);
  }, [handlePaymentSuccess, handlePaymentFail]);

  const validatePhoneNumber = (phoneNum: string) => {
    if (phoneNum === '') {
      setPhoneNumberHelper('נא להזין מספר טלפון');
      setPhoneNumberValid(false);
      return;
    }
    const reg = new RegExp('^05\\d{8}$');
    const isValid = reg.test(phoneNum);
    if (!isValid) {
      setPhoneNumberValid(false);
      setPhoneNumberHelper('מספר הטלפון חייב להתחיל ב-05 ולהכיל בדיוק 10 ספרות');
    } else {
      setPhoneNumberValid(true);
      setPhoneNumberHelper('');
    }
    setPhoneNum(phoneNum);
  };

  const validateLicenseNumber = (licenseNumber: string) => {
    if (licenseNumber === '') {
      setLicenseNumberValid(false);
      setLicenseNumberHelper('נא להזין מספר רשיון');
      return;
    }
    const reg = new RegExp('^\\d{10}$');
    const isValid = reg.test(licenseNumber);
    if (!isValid) {
      setLicenseNumberValid(false);
      setLicenseNumberHelper('מספר רשיון נשק חייב להכיל 10 ספרות בלבד');
    } else {
      setLicenseNumberValid(true);
      setLicenseNumberHelper('');
    }
    setLicenseNumber(licenseNumber);
  };

  const validateFirstName = (firstName: string) => {
    if (firstName === '') {
      setFirstNameValid(false);
      setFirstNameHelper('נא להזין שם פרטי');
      return;
    }
    const reg = new RegExp('^[א-ת]+( {0,1}[א-ת]+)*$');
    const isValid = reg.test(firstName);
    if (!isValid) {
      setFirstNameValid(false);
      setFirstNameHelper('שם פרטי חייב להיות בעברית, ללא תווים מיוחדים וללא רווח בהתחלה או בסוף');
    } else {
      setFirstNameValid(true);
      setFirstNameHelper('');
    }
    setFirstName(firstName);
  };

  const validateLastName = (lastName: string) => {
    if (lastName === '') {
      setLastNameValid(false);
      setLastNameHelper('נא להזין שם משפחה');
      return;
    }
    const reg = new RegExp('^[א-ת]+( {0,1}[א-ת]+)*$');
    const isValid = reg.test(lastName);
    if (!isValid) {
      setLastNameValid(false);
      setLastNameHelper('שם משפחה חייב להיות בעברית, ללא תווים מיוחדים וללא רווח בהתחלה או בסוף');
    } else {
      setLastNameValid(true);
      setLastNameHelper('');
    }
    setLastName(lastName);
  };

  const validateOtherCaliber = (caliber: string) => {
    if (caliber === '') {
      setOtherCaliberValid(false);
      setOtherCaliberHelper('נא להזין קליבר');
      return;
    } else {
      setOtherCaliberHelper('');
      setOtherCaliberValid(true);
    }
    setOtherCaliber(caliber);
  };

  return (
    <div style={{ margin: 'auto', textAlign: 'center', marginTop: '2%', maxWidth: '80%' }}>
      {currentWorkout?.id === undefined ? (
        <img
          src="/FadingCircles.gif"
          alt="spinner"
          style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', marginTop: '20%' }}
        />
      ) : currentWorkout.isActive ? (
        <>
          <h1>הרשמה לאימון</h1>
          <br />
          {currentWorkout?.type?.id === undefined ? (
            <>הנתונים בטעינה אנא המתן</>
          ) : (
            <div className="details">
              <div className="details-grid">
                <div className="details-div1">
                  סוג האימון: {currentWorkout.type?.name}
                  <br />
                  תאריך: {changeDateFormat(currentWorkout?.date)}
                  <br />
                  שעת התחלה: {currentWorkout?.time.slice(0, -3)}
                  <br />
                  מדריך: {currentWorkout.instructor?.fullName}
                  <br />
                  מיקום: {currentWorkout?.location}
                  <br />
                  {currentWorkout.price === 0 ? '' : <>מחיר: {currentWorkout.price} ש"ח</>}
                </div>
                <br />
                <br />
                <div
                  className="details-div2"
                  style={{ whiteSpace: 'pre-wrap', textAlign: 'right', lineHeight: '30px' }}
                >
                  <p style={{ textAlign: 'center' }}>תנאי האימון:</p>
                  {currentWorkout?.terms}
                </div>
              </div>
            </div>
          )}
          {new Date(`${currentWorkout.date} ${currentWorkout.time}`) > new Date() ? (
            <>
              {(currentWorkout?.maximumParticipants as number) -
                (currentWorkout?.currentParticipantsCount as number) !==
                0 ? (
                <>
                  <hr />
                  <form method="POST" id="register" onSubmit={(e) => e.preventDefault()}>
                    <FormControlLabel
                      required
                      control={
                        <Checkbox
                          onChange={(e) => setIsTermsChecked(e.target.checked)}
                          checked={isTermsChecked}
                        />
                      }
                      label="קראתי והבנתי את התנאים של האימון"
                    />
                    <br />
                    פרטי הנרשם:
                    <br />
                    <br />
                    <div className="form-grid">
                      <TextField
                        className="form-div1"
                        variant="filled"
                        label="שם פרטי"
                        type="text"
                        onChange={(e) => validateFirstName(e.target.value)}
                        error={!FirstNameValid}
                        helperText={FirstNameHelper}
                        required
                      />
                      <TextField
                        className="form-div2"
                        variant="filled"
                        label="שם משפחה"
                        type="text"
                        onChange={(e) => validateLastName(e.target.value)}
                        error={!LastNameValid}
                        helperText={LastNameHelper}
                        required
                      />
                      <TextField
                        className="form-div3"
                        variant="filled"
                        label='כתובת דוא"ל'
                        type="email"
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <TextField
                        className="form-div4"
                        variant="filled"
                        label="מספר טלפון נייד"
                        type="tel"
                        onChange={(e) => validatePhoneNumber(e.target.value)}
                        error={!PhoneNumberValid}
                        helperText={PhoneNumberHelper}
                        required
                      />
                      <div className="form-div5">
                        <TextField
                          variant="filled"
                          label="קליבר"
                          select
                          value={Caliber ?? ''}
                          onChange={(e) => setCaliber(e.target.value)}
                          required
                          placeholder="בחר קליבר"
                          style={{
                            textAlign: 'center',
                            width: Caliber === 'אחר' ? '20%' : '100%',
                          }}
                        >
                          {calibers.map((value, index) => (
                            <MenuItem key={index} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </TextField>
                        {Caliber === 'אחר' && (
                          <TextField
                            variant="filled"
                            label="קליבר - אחר"
                            type="text"
                            onChange={(e) => validateOtherCaliber(e.target.value)}
                            error={!otherCaliberValid}
                            helperText={otherCaliberHelper}
                            required
                            style={{ textAlign: 'center', width: '80%' }}
                          />
                        )}
                      </div>
                      {currentWorkout.fullForm && (
                        <>
                          <TextField
                            className="form-div6"
                            variant="filled"
                            label="מספר רשיון"
                            type="text"
                            onChange={(e) => validateLicenseNumber(e.target.value)}
                            error={!LicenseNumberValid}
                            helperText={LicenseNumberHelper}
                            required
                          />
                          <TextField
                            className="form-div7"
                            variant="filled"
                            label="תוקף רשיון"
                            type="date"
                            slotProps={{
                              htmlInput: { min: new Date().toISOString().slice(0, 10) },
                              inputLabel: { shrink: true },
                            }}
                            onChange={(e) => setLicenseExpiryDate(e.target.value)}
                            required
                          />
                          <TextField
                            className="form-div8"
                            variant="filled"
                            label="סוג אקדח"
                            type="text"
                            onChange={(e) => setPistolType(e.target.value)}
                            required
                          />
                          <TextField
                            className="form-div9"
                            variant="filled"
                            label="מספר נשק"
                            type="text"
                            onChange={(e) => setPistolNumber(e.target.value)}
                            required
                          />
                        </>
                      )}
                      <TextField
                        className={
                          currentWorkout.fullForm ? 'form-div10' : 'form-div10-conditional'
                        }
                        variant="filled"
                        label="הערות"
                        type="text"
                        onChange={(e) => setNotes(e.target.value)}
                      />
                      <Button
                        className={
                          currentWorkout.fullForm ? 'form-div11' : 'form-div11-conditional'
                        }
                        id="submit"
                        type="button"
                        disabled={isSubmitting}
                        onClick={handleProceedToPayment}
                        variant="contained"
                        size="large"
                        color="success"
                      >
                        {isSubmitting
                          ? 'שומר פרטים...'
                          : currentWorkout.price === 0
                            ? 'הירשם לאימון'
                            : 'מעבר לתשלום ורישום'}
                      </Button>
                    </div>
                  </form>

                  <TranzilaPaymentModal
                    open={isPaymentOpen}
                    onClose={handleRevertRegistration}
                    paymentUrl={paymentUrl}
                  />
                </>
              ) : (
                <h1>לא ניתן להירשם, האימון מלא</h1>
              )}
            </>
          ) : (
            <h1>מועד האימון חלף, לא ניתן להירשם</h1>
          )}
        </>
      ) : (
        <h1>האימון מבוטל, עמכם הסליחה</h1>
      )}
    </div>
  );
};

export default Register;